import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const CommunityEvents = () => {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  const openLightbox = (src, alt = '') => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
  };

  const closeLightbox = () => {
    setLightboxSrc(null);
    setLightboxAlt('');
  };

  useEffect(() => {
    if (!lightboxSrc) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', handleKeyDown);
    // Prevent background scroll
    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      style.overflow = previousOverflow;
    };
  }, [lightboxSrc]);

  return (
    <main id="main-content" className="bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                Community
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                Community <span className="font-bold">Events</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Bringing our school community together through meaningful events and celebrations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Community <span className="font-bold text-[#630503]">Events at P.S. 11X</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Our community events bring families, students, staff, and neighborhood partners together to celebrate learning,
              wellness, culture, and service. Throughout the year, we host experiences that strengthen connections and support
              the whole child.
            </p>
          </div>

          {/* Event Types */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Family Engagement</h3>
              <p className="text-gray-700 leading-relaxed">Workshops, family nights, and shared learning experiences designed to build strong school–home partnerships.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Student Showcases</h3>
              <p className="text-gray-700 leading-relaxed">Events that highlight scholar voice, creativity, and achievement across the arts, academics, and athletics.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Wellness & Safety</h3>
              <p className="text-gray-700 leading-relaxed">Community resources and supports that promote physical, social, and emotional wellness for all families.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Community Partnerships</h3>
              <p className="text-gray-700 leading-relaxed">Collaborations with neighborhood organizations that enrich learning and expand opportunities for our scholars.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              How <span className="font-bold text-[#630503]">Events Work</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Communication</h3>
              <p className="text-gray-700">Event details are shared on our website and through school communications. Check the calendar for dates and updates.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Accessibility</h3>
              <p className="text-gray-700">We strive to make events welcoming and accessible for all families. Interpretation and accommodations are available when possible.</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Get Involved</h3>
              <p className="text-gray-700">Families and community partners are encouraged to participate and volunteer. Your involvement makes our events thrive.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-700">
              For dates and scheduling, visit our{' '}
              <Link to="/calendar" className="text-[#630503] font-semibold hover:underline">School Calendar</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Event Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900 mb-8">
              Event <span className="font-bold text-[#630503]">Highlights</span>
            </h2>
            <div className="w-24 h-px bg-[#630503]/40 mx-auto"></div>
          </div>

          <div className="space-y-12">
            {/* Family Wellness Day */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Family Wellness Day</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                A day focused on wellness, connection, and support for the whole family. Explore the flyer to see featured
                activities and resources available during the event.
              </p>
              <div className="max-w-3xl mx-auto">
                <a href="/assets/documents/communityevents/JOY FWD 2025.pdf" target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg border group">
                  <div className="relative w-full h-64">
                    <iframe
                      src="/assets/documents/communityevents/JOY FWD 2025.pdf"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      title="Family Wellness Day 2025 Flyer Preview"
                    />
                  </div>
                  <div className="px-4 py-3 bg-gray-50 border-t text-center text-sm text-gray-700 group-hover:underline">
                    Click to view the full flyer
                  </div>
                </a>
              </div>
            </div>

            {/* Inaugural Event – Heroes of Highbridge Walk/Run */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Inaugural Event – Heroes of Highbridge Walk/Run</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                The Saturday before Thanksgiving, District 9—together with Highbridge Green and P.S. 11X—organized a 2-mile
                community Walk/Run to honor the Heroes of Historic Highbridge. With more than 500 participants, families made it
                clear: health-centered community events are exactly what the neighborhood wants.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-hidden">
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/walk-1.jpg', 'Heroes of Highbridge Walk/Run1')} src="/assets/images/communityevents/walk-1.jpg" alt="Heroes of Highbridge Walk/Run1" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/walk-2.jpg', 'Heroes of Highbridge Walk/Run2')} src="/assets/images/communityevents/walk-2.jpg" alt="Heroes of Highbridge Walk/Run2" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/walk-3.jpg', 'Heroes of Highbridge Walk/Run3')} src="/assets/images/communityevents/walk-3.jpg" alt="Heroes of Highbridge Walk/Run3" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/walk-4.jpg', 'Heroes of Highbridge Walk/Run4')} src="/assets/images/communityevents/walk-4.jpg" alt="Heroes of Highbridge Walk/Run4" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
              </div>
            </div>

            {/* Dad's and Donuts (June) */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] via-[#A0522D] to-[#630503]"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dad's and Donuts (June)</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                A joyful morning celebrating father figures and caregivers—sharing conversation, connection, and of course,
                donuts. An uplifting tradition that highlights the power of family engagement.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/dad-1.jpg', "Dad's and Donuts1")} src="/assets/images/communityevents/dad-1.jpg" alt="Dad's and Donuts1" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/dad-2.jpg', "Dad's and Donuts2")} src="/assets/images/communityevents/dad-2.jpg" alt="Dad's and Donuts2" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/dad-3.jpg', "Dad's and Donuts3")} src="/assets/images/communityevents/dad-3.jpg" alt="Dad's and Donuts3" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
              </div>
            </div>

            {/* Dads Bring Your Child to School (September) */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A0522D] via-[#8B4513] to-[#630503]"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dads Bring Your Child to School (September)</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                A warm welcome to a new school year. Fathers and caregivers led scholars into the building, modeling
                encouragement and pride as we launched another year of learning together.
              </p>
              <div className="max-w-3xl mx-auto">
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/dad-4.png', 'Dads Bring Your Child to School')} src="/assets/images/communityevents/dad-4.png" alt="Dads Bring Your Child to School" className="w-full h-64 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
              </div>
            </div>

            {/* MLK Day of Service */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#630503] via-[#8B4513] to-[#A0522D]"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">MLK Day of Service</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                On Martin Luther King Jr. Day, children and families assembled buddy bags for neighbors experiencing
                homelessness. After reflective writing and discussion on Dr. King's message, students shared powerful
                reflections and closed in song—building community through service.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/mlk-1.png', 'MLK Day of Service1')} src="/assets/images/communityevents/mlk-1.png" alt="MLK Day of Service1" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/mlk-2.jpg', 'MLK Day of Service2')} src="/assets/images/communityevents/mlk-2.jpg" alt="MLK Day of Service2" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/mlk-3.jpg', 'MLK Day of Service3')} src="/assets/images/communityevents/mlk-3.jpg" alt="MLK Day of Service3" className="w-full h-48 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
              </div>
            </div>

            {/* District 9 Day of Community Service */}
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B4513] via-[#A0522D] to-[#630503]"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">District 9 Day of Community Service</h3>
              <p className="text-gray-700 leading-relaxed mb-8">
                Student Councils from P.S. 11X and P.S. 199 teamed up to care for our local community garden. Student leaders
                spent the day cleaning, planting, digging, and transforming the space into something beautiful for all.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/service-1.jpg', 'District 9 Day of Community Service1')} src="/assets/images/communityevents/service-1.jpg" alt="District 9 Day of Community Service1" className="w-full h-64 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
                <div className="overflow-hidden rounded-lg border"><img onClick={() => openLightbox('/assets/images/communityevents/service-2.jpg', 'District 9 Day of Community Service2')} src="/assets/images/communityevents/service-2.jpg" alt="District 9 Day of Community Service2" className="w-full h-64 object-cover hover:scale-[1.02] transition-transform cursor-zoom-in" /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal (match Gallery sizing/behavior) */}
      {lightboxSrc && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
        >
          <div 
            className="relative max-w-5xl max-h-full bg-white rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-[#630503] text-white px-6 py-4 flex justify-end items-center">
              <button
                onClick={closeLightbox}
                className="text-white hover:text-gray-300 transition-colors"
                type="button"
              >
                Close
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[80vh] overflow-auto">
              <img 
                src={lightboxSrc}
                alt={lightboxAlt || 'Expanded view'}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '70vh' }}
              />
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end items-center">
                <button
                  onClick={closeLightbox}
                  className="bg-[#630503] text-white px-4 py-2 rounded-lg hover:bg-[#630503]/90 transition-colors"
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default CommunityEvents;
